<script lang="ts" setup>
import { ref } from 'vue'
import { usePwa } from '../composable'

const { canInstall, promptInstall } = usePwa()
const isDismissed = ref(false)

const handleInstall = async (): Promise<void> => {
  await promptInstall()
  isDismissed.value = true
}

const handleDismiss = (): void => {
  isDismissed.value = true
}
</script>

<template>
  <transition
    enter-active-class="transition-all duration-200 ease-out"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div
      v-if="canInstall && !isDismissed"
      class="fixed bottom-4 right-4 max-w-sm border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800 z-50"
      data-testid="pwa-install-prompt"
    >
      <div class="p-4">
        <div class="flex items-start justify-between mb-3">
          <div class="flex-1">
            <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">Install App</h3>
            <p class="text-sm text-slate-600 dark:text-slate-400">
              Install this app on your device for quick access and offline use.
            </p>
          </div>
          <button
            aria-label="Dismiss"
            class="ml-3 w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
            data-testid="pwa-dismiss-button"
            @click="handleDismiss"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
        <div class="flex gap-2">
          <button
            class="flex-1 px-4 py-2 text-sm border border-slate-900 bg-slate-900 text-white hover:bg-slate-800 hover:border-slate-800 dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 dark:hover:border-slate-200 transition-all duration-150"
            data-testid="pwa-install-button"
            @click="handleInstall"
          >
            Install
          </button>
          <button
            class="px-4 py-2 text-sm border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:border-slate-600 transition-all duration-150"
            @click="handleDismiss"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>
